import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PaymentProviderId } from '@marxbiotech/shared-types/bff';
import { buildCartFromOrder } from '@lib/util/build-cart-from-order';
import { getPromotionsFromCart } from '@lib/util/get-promotions-from-cart';
import CartCollapseContainer from '@modules/checkout/components/cart-collapse-container';
import CheckoutCart from '@modules/checkout/components/checkout-cart';
import ActionBar from '@modules/common/components/action-bar';
import Container from '@modules/common/components/container';
import Stepper from '@modules/common/components/stepper';
import OrderConfirmedDetails from '@modules/order/components/order-confirmed-details';
import type { OrderDetailData } from '@modules/order/components/order-details/types';

interface OrderCompletedTemplateProps {
  order: OrderDetailData;
}

const OrderCompletedTemplate = ({ order }: OrderCompletedTemplateProps) => {
  const navigate = useNavigate();
  const { countryCode = 'tw' } = useParams<{ countryCode: string }>();
  const isCod =
    order.payment_collections?.[0]?.payments?.[0]?.provider_id ===
    PaymentProviderId.COD;
  const cart = useMemo(() => buildCartFromOrder(order), [order]);
  const prevAction = isCod
    ? {
        text: '查看訂單詳情',
        action: () => navigate(`/${countryCode}/account/orders/details/${order.id}`),
      }
    : {
        text: '查看儲值金紀錄',
        action: () => navigate(`/${countryCode}/account/marx-payment-account`),
      };
  return (
    <div className="min-h-[calc(100vh-64px)] py-6">
      <Container className="flex min-h-[90vh] w-full flex-col items-center justify-center gap-y-10">
        <div
          className="flex h-full w-full max-w-[1440px] flex-col gap-4 bg-white py-10 px-4"
          data-testid="order-complete-container"
        >
          <div className="mb-12 flex justify-center">
            <Stepper
              steps={['購物車', '填寫購買資訊', '確認訂單']}
              activeStep={3}
            />
          </div>
          <div className="mb-12">
            <CartCollapseContainer cart={cart}>
              <CheckoutCart
                cart={cart}
                promotions={getPromotionsFromCart(order.cart)}
              />
            </CartCollapseContainer>
          </div>

          <OrderConfirmedDetails order={order} />
        </div>
      </Container>
      <ActionBar
        className="sticky bottom-2 w-full"
        prevBtn={{
          ...prevAction,
          'data-testid': 'order-complete-prev-button',
        }}
        nextBtn={{
          text: '回首頁',
          'data-testid': 'order-complete-home-button',
          action: () => navigate(`/${countryCode}`),
        }}
      />
    </div>
  );
};

export default OrderCompletedTemplate;
